import { FilterTypeModel } from './abstract-filter-type.model';
import { FilterTypesEnum } from '../enums/filters-types.enum';
import { ChipModel } from '../../Forms/models/chip.model';
import { RangeModel } from './range.model';
import { FilterQuickOptionModel } from './filter-quick-option.model';
import { formatNumber } from '@angular/common';
import { filter } from 'underscore';

export class FilterTypeRangeModel extends FilterTypeModel<RangeModel> {
    readonly key: FilterTypesEnum = FilterTypesEnum.range;
    min: number;
    max: number;
    unitIcon: string;
    currency: boolean = false;
    integerOnly: boolean;
    percentType: boolean = false;
    allowZero: boolean = false;
    allowNullMin: boolean = false;
    fileType: boolean = false;

    readonly MAX: number = 999999999999;

    value: RangeModel;
    currentValue: RangeModel;

    constructor(
        id: string,
        label: string,
        min?: number,
        max?: number,
        unitIcon?: string,
        currency?: boolean,
        value?: RangeModel,
        integerOnly?: boolean,
        percentType?: boolean,
        section?: string,
        allowZero?: boolean,
        allowNullMin?: boolean,
        fileType?: boolean
    ) {
        super(id, label, section);

        this.min = min;
        this.max = max;
        this.unitIcon = unitIcon;
        this.currency = currency;
        this.setValue(value || {});
        this.integerOnly = integerOnly;
        this.percentType = percentType;
        this.allowZero = !!allowZero;
        this.allowNullMin = !!allowNullMin;
        this.fileType = fileType;
    }

    /**
     * Makes a chip from a range
     * @param val A range
     */
    makeChip(val: RangeModel): ChipModel|null {
        if (this.isNaNOrNull(val.min) && this.isNaNOrNull(val.max)) {
            return null;
        } else if (this.isNaNOrNull(val.min)) {
            return {
                label: this.percentType ? 'Common.filter.range.percent.lessThan' : 'Common.filter.range.lessThan',
                labelArgs: {
                    name: this.label,
                    max: formatNumber(val.max, 'en')
                }
            };
        } else if (this.isNaNOrNull(val.max)) {
            return {
                label: this.percentType ? 'Common.filter.range.percent.greaterThan' : 'Common.filter.range.greaterThan',
                labelArgs: {
                    name: this.label,
                    min: formatNumber(val.min, 'en')
                }
            };
        }
        return {
            label: this.percentType ? 'Common.filter.range.percent.minToMax' : 'Common.filter.range.minToMax',
            labelArgs: {
                name: this.label,
                min: formatNumber(val.min, 'en'),
                max: formatNumber(val.max, 'en')
            }
        };
    }

    /**
     * because isNaN(null) will return false, we need this method to exclude null for the value
     * @param n the number
     * @returns if n is a number (not null AND not undefined) return true, else false
     */
    public isNaNOrNull(n: number): boolean{
        if (n === null){
            return true;
        }
        return isNaN(n);
    }

    /**
     * Sets the active value for a filter
     * @param value
     */
    setValue(value: RangeModel): void {
        this.value = value;

        // if null is allowed for min value, we use null instead of undefined
        if (this.allowNullMin) {
            this.value.min = this.value.min === undefined ? null : this.value.min;
        }

        // setting it to undefined if it uses the max value, otherwise it will displays: min to 999999999999
        this.value.max = this.value.max === this.MAX ? undefined : this.value.max;
        if (this.allowZero) {
            this.value.min = this.value.min < 0 ? undefined : this.value.min;
        } else {
            this.value.min = this.value.min === 0 ? undefined : this.value.min;
        }

        this.chips = [];
        const chip = this.makeChip(this.value);
        if (chip) {
            this.chips.push(chip);
        }
        this.cancelChanges();
    }

    /**
     * Gets the active value for a filter
     */
    getValue(): RangeModel {
        return this.value;
    }

    /**
     * Handles deleting a chip for the active value of this filter
     * @param chip A chip from this filter
     */
    removeChip(chip: ChipModel): void {
        this.setValue({});
    }

    /**
     * Resets the filter to not apply any changes
     */
    clear(): void {
        this.setValue({});
    }

    /**
     * Sets the current value for a filter
     * @param value
     */
    setCurrentValue(value: RangeModel): void {
        this.currentValue = value;
        this.currentChips = [];
        const chip = this.makeChip(this.currentValue);
        if (chip) {
            this.currentChips.push(chip);
        }
    }

    /**
     * Gets the current value for a filter
     */
    getCurrentValue(): RangeModel {
        return this.currentValue;
    }

    /**
     * Handles deleting a chip for the current value of this filter
     * @param chip A chip from this filter
     */
    removeCurrentChip(chip: ChipModel): void {
        this.setCurrentValue({});
    }

    /**
     * Resets the current value to the active value
     */
    cancelChanges(): void {
        this.setCurrentValue({min: this.value.min, max: this.value.max});
    }
    quickAutocomplete(query: string, translateMethod: (str: string) => string): FilterQuickOptionModel<RangeModel>[] {
        if (query.match(/>\d+/)) {
            const minimum = +query.slice(1);
            return [
                {
                    label: query,
                    value: { min: minimum },
                    filter: this
                }
            ];
        } else if (query.match(/<\d+/)) {
            const maximum = +query.slice(1);
            return [
                {
                    label: query,
                    value: { max: maximum },
                    filter: this
                }
            ];
        } else if (query.match(/\d+-\d+/)) {
            const [minimum, maximum] = query.split('-');
            return [
                {
                    label: query,
                    value: { min: +minimum, max: +maximum },
                    filter: this
                }
            ];
        }
        return [];
    }
    addQuickOption(option: FilterQuickOptionModel<RangeModel>): void {
        this.setValue(option.value);
    }
}
